import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Icon } from './Icon';
import {
  BookOpen, Check, ChevronDown, ChevronRight, Clock, Film, GitBranch,
  Globe, Keyboard, LayoutDashboard, Loader, MapPin, Monitor, PenLine,
  Plus, QrCode, RotateCcw, Scissors, Settings, Shield, Sparkles, Trash2,
  User, Download, X, Copy,
} from 'lucide-react';
import { useToast } from '../toastContext';
import { useConfirm } from '../confirmContext';
import Button from './ui/Button';
import Badge from './ui/Badge';
import Input from './ui/Input';
import Select from './ui/Select';

const CATEGORY_ICONS = {
  'Admin':          Settings,
  'Clips':          Scissors,
  'Dashboard':      LayoutDashboard,
  'Editor':         PenLine,
  'Export':          Download,
  'Family Tree':    GitBranch,
  'General':        Keyboard,
  'Lang Files':     Globe,
  'Places':         MapPin,
  'Prompt Library': BookOpen,
  'QA':             Sparkles,
  'QR / Memorial':  QrCode,
  'Subject Bio':    User,
  'Timeline':       Clock,
};

const LOCKED_ADMIN_PERMISSIONS = new Set([
  'admin.dashboard',
  'admin.user_management',
]);

const BUILT_IN_ROLES = new Set(['viewer', 'editor', 'admin']);

const DEFAULT_ROLE_COLORS = {
  viewer: '#6b7280',
  editor: '#3b82f6',
  admin:  '#f59e0b',
};

const PRESET_COLORS = ['#ef4444', '#f97316', '#f59e0b', '#22c55e', '#06b6d4', '#3b82f6', '#8b5cf6', '#ec4899', '#6b7280', '#84cc16'];

function roleColor(role, roleMeta) {
  if (DEFAULT_ROLE_COLORS[role]) return DEFAULT_ROLE_COLORS[role];
  const meta = (roleMeta || []).find(m => m.name === role);
  return meta?.color || '#6b7280';
}

function roleDisplayName(role, roleMeta) {
  const meta = (roleMeta || []).find(m => m.name === role);
  return meta?.display_name || role.charAt(0).toUpperCase() + role.slice(1);
}

export default function AccessControlBoard({ axiosAuth, theme }) {
  const { pushToast } = useToast();
  const confirm = useConfirm();
  const [grid, setGrid] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [saved, setSaved] = useState({});
  const [collapsed, setCollapsed] = useState({});
  const [showAddRole, setShowAddRole] = useState(false);
  const [newRole, setNewRole] = useState({ display_name: '', base_role: 'editor', color: '#3b82f6', description: '' });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    axiosAuth.get('/admin/permissions')
      .then(r => { setGrid(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [axiosAuth]);

  const grouped = useMemo(() => {
    if (!grid?.permissions) return [];
    const map = new Map();
    for (const p of grid.permissions) {
      if (!map.has(p.category)) map.set(p.category, []);
      map.get(p.category).push(p);
    }
    return [...map.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([cat, perms]) => [cat, perms.sort((a, b) => a.label.localeCompare(b.label))]);
  }, [grid]);

  const overrideCount = useMemo(() => {
    if (!grid?.permissions) return 0;
    return grid.permissions.reduce((count, p) =>
      count + Object.values(p.overridden).filter(Boolean).length, 0);
  }, [grid]);

  const togglePermission = useCallback((role, permKey, currentValue) => {
    const cellKey = `${permKey}_${role}`;
    setSaving(s => ({ ...s, [cellKey]: true }));
    axiosAuth.put('/admin/permissions', {
      updates: [{ role, permission: permKey, allowed: !currentValue }]
    })
      .then(r => {
        setGrid(r.data);
        setSaved(s => ({ ...s, [cellKey]: true }));
        setTimeout(() => setSaved(s => ({ ...s, [cellKey]: false })), 800);
      })
      .catch(() => pushToast('permission-update-error', 'error', 'Failed to update permission'))
      .finally(() => setSaving(s => ({ ...s, [cellKey]: false })));
  }, [axiosAuth, pushToast]);

  const resetAll = useCallback(async () => {
    if (!await confirm({
      message: 'Reset all permissions to defaults? This removes all customizations for built-in roles.\nCustom roles and their permissions will be preserved.',
      title: 'Reset all permissions?',
      confirmLabel: 'Reset',
      destructive: true,
    })) return;
    axiosAuth.post('/admin/permissions/reset', {})
      .then(r => setGrid(r.data))
      .catch(() => pushToast('permission-reset-error', 'error', 'Reset failed'));
  }, [axiosAuth, pushToast, confirm]);

  const toggleCategory = useCallback((cat) => {
    setCollapsed(c => ({ ...c, [cat]: !c[cat] }));
  }, []);

  const createRole = useCallback(() => {
    if (!newRole.display_name.trim()) return;
    setCreating(true);
    axiosAuth.post('/admin/roles', {
      name: newRole.display_name.trim().toLowerCase().replace(/\s+/g, '_'),
      display_name: newRole.display_name.trim(),
      base_role: newRole.base_role,
      color: newRole.color,
      description: newRole.description,
    })
      .then(() => {
        setShowAddRole(false);
        setNewRole({ display_name: '', base_role: 'editor', color: '#3b82f6', description: '' });
        // Reload grid to include new role column
        return axiosAuth.get('/admin/permissions');
      })
      .then(r => setGrid(r.data))
      .catch(e => pushToast('role-create-error', 'error', e?.response?.data?.detail || 'Failed to create role'))
      .finally(() => setCreating(false));
  }, [axiosAuth, newRole, pushToast]);

  const deleteRole = useCallback(async (roleName) => {
    const meta = (grid?.role_meta || []).find(m => m.name === roleName);
    const displayName = meta?.display_name || roleName;
    if (!await confirm({
      message: `Delete custom role "${displayName}"?\n\nAll permission overrides for this role will be removed.\nUsers assigned this role will need to be reassigned.`,
      title: 'Delete custom role?',
      confirmLabel: 'Delete',
      destructive: true,
    })) return;
    axiosAuth.delete(`/admin/roles/${roleName}`)
      .then(() => axiosAuth.get('/admin/permissions'))
      .then(r => setGrid(r.data))
      .catch(e => pushToast('role-delete-error', 'error', e?.response?.data?.detail || 'Failed to delete role'));
  }, [axiosAuth, grid, pushToast, confirm]);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px', gap: '8px', color: theme.textSub }}>
        <Icon icon={Loader} size={16} style={{ animation: 'spin 1s linear infinite' }} /> Loading permissions...
      </div>
    );
  }

  if (!grid) {
    return (
      <div style={{ padding: '20px', color: theme.textSub, textAlign: 'center', fontSize: '0.8rem' }}>
        Failed to load permissions.
      </div>
    );
  }

  const roles = grid.roles || ['viewer', 'editor', 'admin'];
  const roleMeta = grid.role_meta || [];
  const customRoles = roleMeta.filter(m => !m.built_in);
  const colWidth = Math.max(70, Math.min(80, 400 / roles.length));

  return (
    <div>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {overrideCount > 0 && (
            <Badge status="warning" size="md" uppercase={false} theme={theme}>
              {overrideCount} override{overrideCount !== 1 ? 's' : ''}
            </Badge>
          )}
          {customRoles.length > 0 && (
            <span style={{ fontSize: '0.62rem', color: theme.textSub }}>
              {customRoles.length} custom role{customRoles.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <button
            onClick={() => setShowAddRole(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: '4px',
              padding: '4px 10px', background: `${theme.glossaryAccent}15`, color: theme.glossaryAccent,
              border: `1px solid ${theme.glossaryAccent}30`, borderRadius: '6px', cursor: 'pointer',
              fontSize: '0.65rem', fontWeight: 700,
            }}
          >
            <Icon icon={Plus} size={11} /> Add Role
          </button>
          {overrideCount > 0 && (
            <Button variant="danger-ghost" size="sm" icon={RotateCcw} onClick={resetAll} theme={theme}>
              Reset Defaults
            </Button>
          )}
        </div>
      </div>

      {/* Add Role Form */}
      {showAddRole && (
        <div style={{
          marginBottom: '12px', padding: '14px', borderRadius: '10px',
          background: theme.card, border: `1px solid ${theme.glossaryAccent}40`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
            <span style={{ fontSize: '0.78rem', fontWeight: 700, color: theme.text }}>New Custom Role</span>
            <Button variant="ghost" size="sm" icon={X} onClick={() => setShowAddRole(false)} theme={theme} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
            <div>
              <label style={{ fontSize: '0.62rem', fontWeight: 700, color: theme.textSub, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Display Name</label>
              <Input
                value={newRole.display_name}
                onChange={e => setNewRole(r => ({ ...r, display_name: e.target.value }))}
                placeholder="e.g. Manager"
                size="sm"
                theme={theme}
              />
            </div>
            <div>
              <label style={{ fontSize: '0.62rem', fontWeight: 700, color: theme.textSub, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Inherits From</label>
              <Select
                value={newRole.base_role}
                onChange={e => setNewRole(r => ({ ...r, base_role: e.target.value }))}
                size="sm"
                theme={theme}
              >
                <option value="viewer">Viewer (least access)</option>
                <option value="editor">Editor (mid access)</option>
                <option value="admin">Admin (full access)</option>
              </Select>
            </div>
          </div>
          <div style={{ marginBottom: '10px' }}>
            <label style={{ fontSize: '0.62rem', fontWeight: 700, color: theme.textSub, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Color</label>
            <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
              {PRESET_COLORS.map(c => (
                <button
                  key={c}
                  onClick={() => setNewRole(r => ({ ...r, color: c }))}
                  aria-label={c}
                  aria-pressed={newRole.color === c}
                  style={{
                    width: '22px', height: '22px', borderRadius: '50%', border: newRole.color === c ? `2px solid ${theme.text}` : '2px solid transparent',
                    background: c, cursor: 'pointer', outline: newRole.color === c ? `2px solid ${c}` : 'none',
                  }}
                />
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <Button variant="secondary" size="sm" onClick={() => setShowAddRole(false)} theme={theme}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={createRole}
              disabled={!newRole.display_name.trim() || creating}
              theme={theme}
            >
              {creating ? 'Creating...' : 'Create Role'}
            </Button>
          </div>
        </div>
      )}

      {/* Scroll container — role columns are fixed-width, so many custom roles would
          otherwise be clipped by the parent card (overflow:hidden) and become untoggleable.
          The inner min-width box is shared by the header grid, the row grids and the
          full-width category buttons so they all stay aligned while scrolled. */}
      <div style={{ overflowX: 'auto' }}>
      <div style={{ minWidth: `${200 + roles.length * colWidth}px` }}>

      {/* Column headers */}
      <div style={{
        display: 'grid', gridTemplateColumns: `1fr repeat(${roles.length}, ${colWidth}px)`,
        gap: '0', borderBottom: `2px solid ${theme.border}`, paddingBottom: '6px', marginBottom: '4px',
      }}>
        <div style={{ fontSize: '0.62rem', fontWeight: 800, color: theme.textSub, textTransform: 'uppercase', letterSpacing: '0.06em', paddingLeft: '8px' }}>
          Feature
        </div>
        {roles.map(r => {
          const color = roleColor(r, roleMeta);
          const isCustom = !BUILT_IN_ROLES.has(r);
          const meta = roleMeta.find(m => m.name === r);
          return (
            <div key={r} style={{ textAlign: 'center', position: 'relative' }}>
              <div style={{
                fontSize: '0.6rem', fontWeight: 800, textTransform: 'uppercase',
                letterSpacing: '0.04em', color,
              }}>
                {roleDisplayName(r, roleMeta)}
              </div>
              {isCustom && meta?.base_role && (
                <div style={{ fontSize: '0.5rem', color: theme.textSub, marginTop: '1px' }}>
                  ({meta.base_role})
                </div>
              )}
              {isCustom && (
                <button
                  onClick={() => deleteRole(r)}
                  title={`Delete ${roleDisplayName(r, roleMeta)}`}
                  style={{
                    position: 'absolute', top: '-6px', right: '-4px',
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: '#ef4444', opacity: 0.7, padding: '6px', display: 'flex',
                  }}
                  onMouseEnter={e => e.currentTarget.style.opacity = '1'}
                  onMouseLeave={e => e.currentTarget.style.opacity = '0.7'}
                >
                  <Icon icon={Trash2} size={12} />
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Permission rows grouped by category */}
      {grouped.map(([category, perms]) => {
        const CatIcon = CATEGORY_ICONS[category] || Shield;
        const isCollapsed = collapsed[category];

        return (
          <div key={category} style={{ marginBottom: '2px' }}>
            <button
              onClick={() => toggleCategory(category)}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: '8px',
                padding: '8px 8px', background: isCollapsed ? 'transparent' : `${theme.glossaryAccent}08`,
                border: 'none', borderBottom: `1px solid ${theme.border}`,
                cursor: 'pointer', borderRadius: isCollapsed ? '0' : '6px 6px 0 0',
              }}
            >
              <Icon icon={isCollapsed ? ChevronRight : ChevronDown} size={12} color={theme.textSub} />
              <Icon icon={CatIcon} size={14} style={{ color: theme.glossaryAccent }} />
              <span style={{ fontSize: '0.72rem', fontWeight: 700, color: theme.text, flex: 1, textAlign: 'left' }}>
                {category}
              </span>
              <span style={{ fontSize: '0.58rem', color: theme.textSub, fontWeight: 600 }}>
                {perms.length}
              </span>
            </button>

            {!isCollapsed && perms.map((perm, idx) => (
              <div
                key={perm.key}
                style={{
                  display: 'grid', gridTemplateColumns: `1fr repeat(${roles.length}, ${colWidth}px)`,
                  gap: '0', padding: '6px 0',
                  borderBottom: idx < perms.length - 1 ? `1px solid ${theme.border}22` : `1px solid ${theme.border}`,
                  transition: 'background 0.1s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = `${theme.card}`}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{ paddingLeft: '36px', fontSize: '0.75rem', color: theme.text, display: 'flex', alignItems: 'center' }}>
                  {perm.label}
                </div>
                {roles.map(role => {
                  const cellKey = `${perm.key}_${role}`;
                  const isAllowed = perm.roles[role];
                  const isOverridden = perm.overridden[role];
                  const isSaving = saving[cellKey];
                  const justSaved = saved[cellKey];
                  const isLocked = role === 'admin' && LOCKED_ADMIN_PERMISSIONS.has(perm.key);
                  const color = roleColor(role, roleMeta);

                  return (
                    <div key={role} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                      <button
                        onClick={() => !isLocked && !isSaving && togglePermission(role, perm.key, isAllowed)}
                        disabled={isLocked || isSaving}
                        title={isLocked ? 'Admin always has this permission' : `${isAllowed ? 'Revoke' : 'Grant'} ${perm.label} for ${roleDisplayName(role, roleMeta)}`}
                        style={{
                          width: '22px', height: '22px',
                          borderRadius: '5px',
                          border: `1.5px solid ${justSaved ? '#22c55e' : isAllowed ? `${color}66` : theme.border}`,
                          background: justSaved ? '#22c55e22' : isAllowed ? `${color}20` : 'transparent',
                          cursor: isLocked ? 'not-allowed' : isSaving ? 'wait' : 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          opacity: isLocked ? 0.4 : 1,
                          transition: 'all 0.15s',
                        }}
                      >
                        {isSaving ? (
                          <Icon icon={Loader} size={12} style={{ animation: 'spin 1s linear infinite' }} />
                        ) : isAllowed ? (
                          <Icon icon={Check} size={14} style={{ color: justSaved ? '#22c55e' : color }} />
                        ) : null}
                      </button>
                      {isOverridden && (
                        <div
                          title="Customized from default"
                          style={{
                            position: 'absolute', top: '2px', right: `${Math.max(2, (colWidth - 30) / 2)}px`,
                            width: '5px', height: '5px', borderRadius: '50%',
                            background: theme.warning,
                          }}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        );
      })}

      </div>
      </div>
    </div>
  );
}
